export type ChartColorPairType = {
    borderColor: string;
    backgroundColor: string;
};

export const CHART_COLOR_PALETTE = [
    '#1976D2',
    '#388E3C',
    '#F57C00',
    '#7B1FA2',
    '#D32F2F',
    '#00796B',
    '#F06292',
    '#5D4037',
    '#616161',
    '#FF6F00',
    '#303F9F',
    '#689F38',
    '#FF8F00',
    '#C2185B',
    '#455A64',
];

export const createColorPair = (color: string, opacity = '20'): ChartColorPairType => ({
    borderColor: color,
    backgroundColor: `${color}${opacity}`,
});

export const getChartColors = (count: number, opacity = '20'): ChartColorPairType[] => {
    const colors: ChartColorPairType[] = [];

    for (let i = 0; i < count; i++) {
        const colorIndex = i % CHART_COLOR_PALETTE.length;
        const color = CHART_COLOR_PALETTE[colorIndex];
        colors.push(createColorPair(color, opacity));
    }

    return colors;
};

export const getChartColorByIndex = (index: number, opacity = '20'): ChartColorPairType => {
    const colorIndex = index % CHART_COLOR_PALETTE.length;
    const color = CHART_COLOR_PALETTE[colorIndex];

    return createColorPair(color, opacity);
};

const hslToHex = (h: number, s: number, l: number): string => {
    const sNorm = s / 100;
    const lNorm = l / 100;

    const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = lNorm - c / 2;

    let r = 0;
    let g = 0;
    let b = 0;

    if (0 <= h && h < 60) {
        r = c;
        g = x;
        b = 0;
    } else if (60 <= h && h < 120) {
        r = x;
        g = c;
        b = 0;
    } else if (120 <= h && h < 180) {
        r = 0;
        g = c;
        b = x;
    } else if (180 <= h && h < 240) {
        r = 0;
        g = x;
        b = c;
    } else if (240 <= h && h < 300) {
        r = x;
        g = 0;
        b = c;
    } else if (300 <= h && h < 360) {
        r = c;
        g = 0;
        b = x;
    }

    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);

    const toHex = (n: number): string => n.toString(16).padStart(2, '0');

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const generateAlgorithmicColor = (index: number): string => {
    const goldenAngle = 137.508;
    const hue = (index * goldenAngle) % 360;

    const saturation = 70 + (index % 4) * 5;
    const lightness = 45 + (index % 3) * 5;

    return hslToHex(hue, saturation, lightness);
};

export const getUniqueChartColors = (count: number, opacity = '20'): ChartColorPairType[] => {
    const colors: ChartColorPairType[] = [];

    for (let i = 0; i < count; i++) {
        let color: string;

        if (i < CHART_COLOR_PALETTE.length) {
            color = CHART_COLOR_PALETTE[i];
        } else {
            color = generateAlgorithmicColor(i - CHART_COLOR_PALETTE.length);
        }

        colors.push(createColorPair(color, opacity));
    }

    return colors;
};

export const getIntelligentChartColors = (count: number, opacity = '20'): ChartColorPairType[] => {
    if (count <= CHART_COLOR_PALETTE.length) {
        return getChartColors(count, opacity);
    }

    return getUniqueChartColors(count, opacity);
};

export function getColorForEventType(eventType: string): string {
    let hash = 0;
    for (const char of eventType) {
        hash = char.charCodeAt(0) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % CHART_COLOR_PALETTE.length;

    return CHART_COLOR_PALETTE[index];
}
